import React from 'react';
import useOneUpHealthRefreshProgress from '../../../hooks/api/useMedicalProfileRefreshProgress';
import { getIntl } from '../../../utils/intl';
import Alert from '../../v2BaseComponents/Alert';
import { Dynamo } from '../../../primary-record-types';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import ProgressBar from '../../v2BaseComponents/ProgressBar';
import { StyleProp, ViewStyle } from 'react-native';

const intl = getIntl('refreshProgress', {
    inProgress: 'Portal Connected!',
    minimalSubtext:
        'Your health data is downloading. We’ve started organizing it in the Health section. Some info is ready now, but the full process may take up to 3 hours',
    progressSubtext: 'Organizing Your Health Story',
});

export type RefreshProgressOwnProps = {
    medicalProfileId: string;
    minimal?: boolean;
    style?: StyleProp<ViewStyle>;
};

export type RefreshProgressProps = RefreshProgressOwnProps;

const RefreshProgress: React.FC<RefreshProgressProps> = ({ medicalProfileId, minimal, style }) => {
    const { progress } = useOneUpHealthRefreshProgress({ medicalProfileId });
    if (!progress) return null;

    if (
        progress.status === 'no-progress-record-found' ||
        progress.progress.status !== 'in-progress' ||
        progress.isRefreshExpired
    ) {
        return null;
    }

    const getProgressValue = () => {
        if (progress.progress.persistProgress.total === 0 || progress.progress.retrievalProgress.total === 0) {
            return 0;
        }

        const combinedStepsTotal =
            progress.progress.persistProgress.total +
            progress.progress.retrievalProgress.total +
            progress.progress.auditLogRetrievalProgress.total;

        const combinedFinishTotal =
            progress.progress.persistProgress.finished +
            progress.progress.retrievalProgress.finished +
            progress.progress.auditLogRetrievalProgress.finished;
        return (combinedFinishTotal / combinedStepsTotal) * 100;
    };

    const getSubText = () => {
        const { persistProgress, retrievalProgress, auditLogRetrievalProgress } = progress.progress;

        const isComplete = (progress: Dynamo.Progress) => {
            return progress.finished === progress.total;
        };

        const isNotStarted = (progress: Dynamo.Progress) => {
            return progress.total === 0;
        };

        if (
            !isComplete(retrievalProgress) ||
            isNotStarted(retrievalProgress) ||
            !isComplete(auditLogRetrievalProgress) ||
            !isComplete(persistProgress)
        ) {
            return intl('progressSubtext');
        }

        return '';
    };

    return (
        <Alert
            type="success"
            title={intl('inProgress')}
            message={intl('minimalSubtext')}
            content={
                minimal ? null : (
                    <Stack vertical fullWidth space={2} align={'center'}>
                        <ProgressBar progress={getProgressValue()} />
                        <Text variant="subtitle-2">{getSubText()}</Text>
                    </Stack>
                )
            }
            testID="refresh-progress-alert"
            style={style}
        />
    );
};

export default React.memo(RefreshProgress);
